import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type {
	IPropsStore,
	IStructureAPI,
	IAppWillLoadPageHandler,
	ViewerModel,
	Experiments,
} from '@wix/thunderbolt-symbols'
import {
	SiteFeatureConfigSymbol,
	BrowserWindowSymbol,
	CurrentRouteInfoSymbol,
	Props,
	StructureAPI,
	ViewerModelSym,
	ExperimentsSymbol,
} from '@wix/thunderbolt-symbols'
import type { Window, CodeEmbedsSiteConfig } from './types'
import { name } from './symbols'
import type { ICurrentRouteInfo } from 'feature-router'
import type { INavigationManager } from 'feature-navigation-manager'
import { NavigationManagerSymbol } from 'feature-navigation-manager'
import { handleCodeEmbeds, removeDynamicPageSSRNodes } from './codeEmbedApi'
import { loadRequireJS } from '@wix/thunderbolt-commons'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const codeEmbedFactory = (
	{ htmlEmbeds, shouldLoadRequireJS }: CodeEmbedsSiteConfig,
	window: Window,
	currentRouteInfo: ICurrentRouteInfo,
	navigationManager: INavigationManager,
	props: IPropsStore,
	structureAPI: IStructureAPI,
	{ siteAssets }: ViewerModel,
	experiments: Experiments
): IAppWillLoadPageHandler => {
	return {
		name: 'codeEmbed',
		async appWillLoadPage({ pageId, contextId }) {
			if (navigationManager.isFirstNavigation()) {
				return
			}

			// remove dynamic page code embeds that were added in the server, if needed they will be re-introduced by
			// client side code
			removeDynamicPageSSRNodes(window)

			const wrapperId = structureAPI.getPageWrapperComponentId(pageId, contextId)

			const addCodeEmbeds = handleCodeEmbeds(
				htmlEmbeds,
				pageId,
				window,
				currentRouteInfo.getPreviousRouterInfo()?.pageId
			)

			props.update({
				[wrapperId]: {
					codeEmbedsCallback: async () => {
						if (shouldLoadRequireJS) {
							await loadRequireJS(window, siteAssets.clientTopology.moduleRepoUrl)
						}
						addCodeEmbeds()
					},
					shouldRunCodEmbedsCallbackOnce: isExperimentOpen(
						experiments,
						'specs.thunderbolt.shouldRunCodEmbedsCallbackOnce'
					),
				},
			})
		},
	}
}

export const CodeEmbed = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		CurrentRouteInfoSymbol,
		NavigationManagerSymbol,
		Props,
		StructureAPI,
		ViewerModelSym,
		ExperimentsSymbol,
	],
	codeEmbedFactory
)
